import { useMutation } from '@apollo/client';
import type { InsertObligationAssessmentResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertObligationAssessmentResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertObligationAssessmentResultTRPCInput } from './useInsertObligationAssessmentResultTRPC';
import { useInsertObligationAssessmentResultTRPC } from './useInsertObligationAssessmentResultTRPC';

export const useInsertObligationAssessmentResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertObligationAssessmentResultGraphQL, graphqlState] = useMutation(
    InsertObligationAssessmentResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'obligation_assessment_result');
        evictField(cache, 'assessment');
        evictField(cache, 'obligation_assessment_result_aggregate');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertObligationAssessmentResultTRPC();

  const insertObligationAssessmentResult = async (
    variables: InsertObligationAssessmentResultTRPCInput
  ): Promise<InsertObligationAssessmentResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertObligationAssessmentResult(variables);
    }

    const result = await insertObligationAssessmentResultGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to insert obligation assessment result');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertObligationAssessmentResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertObligationAssessmentResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
